'use client';

import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../components/typography';
import { Badge } from '../../components/badge';
import { Card } from '../../components/card';
import { IconButton } from '../../components/icon-button';
import {
  MenuWithItems,
  type MenuItemProps,
} from '../../components/styled/menu';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { MoreVerticalIcon, Edit03Icon, Delete01Icon } from '@terra/ui-icons';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { formatAppliesToSummary } from './applies-to.util';
import type {
  TestingInstructionCardActionsVariant,
  TestingInstructionNote,
} from './types';

type TestingInstructionCardProps = {
  note: TestingInstructionNote;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  actionsVariant?: TestingInstructionCardActionsVariant;
};

export const TestingInstructionCard = ({
  note,
  canManage,
  onEdit,
  onDelete,
  actionsVariant = 'inline',
}: TestingInstructionCardProps) => {
  const t = useTranslations('designSystem.testingInstructions');
  const tCommon = useTranslations('designSystem.common');
  const appliesToSummary = formatAppliesToSummary(
    {
      applyMode: note.applyMode,
      pipelines: note.pipelines,
      endpointGlobs: note.endpointGlobs,
      description: note.description,
    },
    {
      allPipelines: t('appliesTo.allPipelines'),
      pipelinesList: (items) => items.join(', '),
      always: t('appliesTo.always'),
      autoAttached: ({ pipelinesLabel, globCount }) =>
        t('appliesTo.autoAttached', {
          pipelines: pipelinesLabel,
          count: globCount,
        }),
      agentRequested: (value) =>
        value?.trim()
          ? t('appliesTo.agentRequestedWithDescription', {
              description: value.trim(),
            })
          : t('appliesTo.agentRequested'),
      manual: t('appliesTo.manual'),
      pipelineLabel: (pipeline) => t(`pipelines.${pipeline}`),
    },
  );
  const menuItems: MenuItemProps[] = useMemo(
    () => [
      {
        value: 'edit',
        label: t('editInstruction'),
        onClick: onEdit,
      },
      {
        value: 'delete',
        label: t('deleteInstruction'),
        onClick: onDelete,
      },
    ],
    [onDelete, onEdit, t],
  );
  const actions =
    canManage && actionsVariant === 'menu' ? (
      <MenuWithItems
        items={menuItems}
        trigger={
          <IconButton size="xs" variant="ghost" aria-label={tCommon('manage')}>
            <MoreVerticalIcon size={16} />
          </IconButton>
        }
      />
    ) : canManage ? (
      <HStack gap={2} flexShrink={0}>
        <IconButton
          size="xs"
          variant="ghost"
          onClick={onEdit}
          aria-label={t('editInstruction')}
        >
          <Edit03Icon size={16} />
        </IconButton>
        <IconButton
          size="xs"
          variant="ghost"
          onClick={onDelete}
          aria-label={t('deleteInstruction')}
        >
          <Delete01Icon size={14} />
        </IconButton>
      </HStack>
    ) : null;

  return (
    <Card.Root
      w="full"
      bg="bg.neutral.default"
      boxShadow="xs"
      borderWidth="1px"
      borderColor="border.neutral.subtle"
    >
      <Box px={4} py={3}>
        <HStack gap={4} alignItems="flex-start" justifyContent="space-between">
          <VStack alignItems="stretch" gap={1.5} flex={1} minW={0}>
            <HStack gap={2} alignItems="center" flexWrap="wrap" minW={0}>
              <BodyStrong color="fg.neutral.default" truncate>
                {note.title}
              </BodyStrong>
              <Badge variant="fill" size="sm">
                {t(`applyModes.${note.applyMode}`)}
              </Badge>
              {note.category ? (
                <Badge variant="outline" size="sm">
                  {t(`categories.${note.category}`)}
                </Badge>
              ) : null}
            </HStack>
            <BodyDefault color="fg.neutral.muted" whiteSpace="pre-wrap">
              {note.content}
            </BodyDefault>
            <MetaDefault color="fg.neutral.subtle" lineClamp={2}>
              {appliesToSummary}
            </MetaDefault>
          </VStack>
          {actions}
        </HStack>
      </Box>
    </Card.Root>
  );
};
